<template>
    <div class="public-client-step-one" data-cy="public-client-step-one">
        <div class="step-one-hero">
            <div class="step-one-hero__content">
                <span class="step-one-hero__eyebrow">Cadastro publico de clientes</span>
                <h4 class="step-one-hero__title">Vamos começar pela identificação do cliente</h4>
                <p class="step-one-hero__text">
                    Informe o CPF ou CNPJ para prosseguir com o cadastro.
                </p>
            </div>

            <div class="step-one-hero__status">
                <span class="step-one-hero__status-icon">
                    <i class="icon-assignment"></i>
                </span>
                <small>Tipo de cadastro</small>
                <strong>{{ selectedPersonLabel }}</strong>
            </div>
        </div>

        <div class="document-card">
            <div class="document-card__main">
                <div class="form-group mb-0">
                    <label for="public_client_taxpayer" class="required">CPF ou CNPJ</label>
                    <div class="document-input">
                        <i class="icon-person document-input__icon"></i>
                        <input
                                id="public_client_taxpayer"
                                v-model="taxpayer"
                                type="text"
                                inputmode="numeric"
                                maxlength="18"
                                class="form-control document-input__control"
                                placeholder="Digite um CPF/CNPJ"
                                data-cy="public-client-taxpayer"
                                @change="focusTaxpayer()"
                                @input="handleTaxpayerInput"
                        >
                    </div>
                    <small class="form-text text-muted">{{ taxpayerHint }}</small>
                </div>
            </div>

            <div class="document-card__options" aria-label="Tipo de pessoa">
                <button
                        type="button"
                        class="person-type-card"
                        :class="{'person-type-card--active': personType === 'natural'}"
                        @click="selectPersonType('natural')"
                >
                    <span><i class="icon-user"></i></span>
                    <strong>Pessoa fisica</strong>
                    <small>CPF e dados pessoais</small>
                </button>

                <button
                        type="button"
                        class="person-type-card"
                        :class="{'person-type-card--active': personType === 'legal'}"
                        @click="selectPersonType('legal')"
                >
                    <span><i class="icon-domain"></i></span>
                    <strong>Pessoa jurídica</strong>
                    <small>CNPJ e dados empresariais</small>
                </button>
            </div>
        </div>

        <transition name="step-one-fade" mode="out-in">
            <div v-if="!shouldShowForm" key="empty" class="empty-state-card">
                <span class="empty-state-card__icon"><i class="icon-edit"></i></span>
                <div>
                    <h5>Os dados principais aparecem aqui</h5>
                    <p>
                        Ao informar um documento valido, o formulário será habilitado.
                    </p>
                </div>
            </div>

            <div v-else-if="personType === 'natural'" key="natural" class="person-form-card">
                <div class="person-form-card__header">
                    <div>
                        <span class="person-form-card__eyebrow">Pessoa fisica</span>
                        <h5>Dados pessoais</h5>
                    </div>
                    <span class="person-form-card__badge">CPF identificado</span>
                </div>

                <div class="row gutters">
                    <div class="col-lg-6 col-md-12">
                        <div class="form-group">
                            <label for="name" class="required">Nome completo</label>
                            <input id="name" v-model="naturalPerson.name" type="text" class="form-control">
                            <div style="color: red" v-if="validate.name"> {{ validate.name }}</div>
                        </div>
                    </div>
                    <div class="col-lg-6 col-md-6">
                        <div class="form-group mb-0">
                            <label for="email" :class="{'required':requiredClassValidation('email')}">E-mail</label>
                            <input id="email" v-model="naturalPerson.email" type="text" class="form-control">
                            <div style="color: red" v-if="validate.email"> {{ validate.email }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="rg" :class="{'required':requiredClassValidation('rg')}">RG</label>
                            <input id="rg" v-model="naturalPerson.rg" type="text" class="form-control">
                            <div style="color: red" v-if="validate.rg"> {{ validate.rg }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="dispatch_date" :class="{'required':requiredClassValidation('dispatch_date')}">{{
                                    t('CLIENTS.DISPATCH_DATE')
                                }}</label>
                            <input id="dispatch_date" v-model="naturalPerson.dispatch_date" type="date"
                                   class="form-control">
                            <div style="color: red" v-if="validate.dispatch_date"> {{ validate.dispatch_date }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="dispatching_agency"
                                   :class="{'required':requiredClassValidation('dispatching_agency')}">{{
                                    t('CLIENTS.DISPATCH_AGENCY')
                                }}</label>
                            <input id="dispatching_agency" v-model="naturalPerson.dispatching_agency" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="validate.dispatching_agency">
                                {{ validate.dispatching_agency }}
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="marital_status_id"
                                   :class="{'required':requiredClassValidation('marital_status_id')}"
                            >{{t('CLIENTS.MARITAL_STATUS') }}
                            </label>
                            <Select2
                                    id="marital_status_id"
                                    @select="selectMaritalStatus($event)"
                                    v-model="naturalPerson.marital_status_id"
                                    :options="[{id: 'selecione', text: 'selecione'}, ...maritalStatusesOptions]"
                                    placeholder="Selecione"
                            />
                            <div style="color: red" v-if="validate.marital_status_id">
                                {{ validate.marital_status_id }}
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="wedding_date" :class="{'required':requiredClassValidation('wedding_date')}">{{
                                    t('CLIENTS.WEDDING_DATE')
                                }}</label>
                            <input id="wedding_date" v-model="naturalPerson.wedding_date" type="date"
                                   class="form-control">
                            <div style="color: red" v-if="validate.wedding_date"> {{ validate.wedding_date }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="marriage_certificate_registration"
                                   :class="{'required':requiredClassValidation('marriage_certificate_registration')}">{{
                                    t('CLIENTS.MARRIAGE_CERTIFICATE_REGISTRATION')
                                }}</label>
                            <input id="marriage_certificate_registration"
                                   v-model="naturalPerson.marriage_certificate_registration" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="validate.marriage_certificate_registration">
                                {{ validate.marriage_certificate_registration }}
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="birthday" :class="{'required':requiredClassValidation('birthday')}">Data de
                                nascimento</label>
                            <input id="birthday" v-model="naturalPerson.birthday" type="date" class="form-control">
                            <div style="color: red" v-if="validate.birthday"> {{ validate.birthday }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group mb-lg-0">
                            <label for="sex" :class="{'required':requiredClassValidation('sex')}">Gênero</label>
                            <Select2
                                    id="sex"
                                    v-model="naturalPerson.sex"
                                    :settings="select2Settings"
                                    :options="optionsSex"
                                    placeholder="Selecione"
                            />
                            <div style="color: red" v-if="validate.sex"> {{ validate.sex }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="profession_id" :class="{'required':requiredClassValidation('profession_id')}">{{
                                    t('CLIENTS.PROFESSION')
                                }}</label>
                            <Select2
                                    id="profession_id"
                                    v-model="naturalPerson.profession_id"
                                    :settings="professionOptions"
                                    placeholder="Selecione"
                            />
                            <div style="color: red" v-if="validate.profession_id"> {{ validate.profession_id }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="income" :class="{'required':requiredClassValidation('income')}">Renda</label>
                            <input id="income" v-model="naturalPerson.income" type="number" class="form-control">
                            <div style="color: red" v-if="validate.income"> {{ validate.income }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="income_family" :class="{'required':requiredClassValidation('income_family')}">Renda
                                Familiar</label>
                            <input id="income_family" v-model="naturalPerson.income_family" type="number"
                                   class="form-control">
                            <div style="color: red" v-if="validate.income_family"> {{ validate.income_family }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="work_on_company"
                                   :class="{'required':requiredClassValidation('work_on_company')}">Empresa em que
                                trabalha</label>
                            <input id="work_on_company" v-model="naturalPerson.work_on_company" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="validate.work_on_company"> {{ validate.work_on_company }}
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group mb-0">
                            <label for="nationality" :class="{'required':requiredClassValidation('nationality')}">{{
                                    t('CLIENTS.NATIONALITY')
                                }}</label>
                            <input id="nationality" v-model="naturalPerson.nationality" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="validate.nationality"> {{ validate.nationality }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group mb-0">
                            <label for="naturalness" :class="{'required':requiredClassValidation('naturalness')}">{{
                                    t('CLIENTS.BIRTH_PLACE')
                                }}</label>
                            <input id="naturalness" v-model="naturalPerson.naturalness" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="validate.naturalness"> {{ validate.naturalness }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group mb-0">
                            <label for="mother_name" :class="{'required':requiredClassValidation('mother_name')}">{{
                                    t('CLIENTS.MOTHER_NAME')
                                }}</label>
                            <input id="mother_name" v-model="naturalPerson.mother_name" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="validate.mother_name"> {{ validate.mother_name }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group mb-0">
                            <label for="father_name" :class="{'required':requiredClassValidation('father_name')}">{{
                                    t('CLIENTS.FATHER_NAME')
                                }}</label>
                            <input id="father_name" v-model="naturalPerson.father_name" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="validate.father_name"> {{ validate.father_name }}</div>
                        </div>
                    </div>
                    <div class="col-lg-8 col-md-6">
                        <div class="form-group mb-0">
                            <label for="description" :class="{'required':requiredClassValidation('description')}">Observação</label>
                            <textarea id="description" v-model="naturalPerson.description" type="text"
                                      class="form-control">
                            </textarea>
                            <div style="color: red" v-if="validate.description"> {{ validate.description }}</div>
                        </div>
                    </div>
                    <div class="col-12">
                        <div class="phone-panel">
                            <div class="phone-panel__header">
                                <span class="phone-panel__icon"><i class="icon-phone"></i></span>
                                <div>
                                    <strong :class="{'required':requiredClassValidation('phones')}">Telefones</strong>
                                    <small>Adicione uma ou mais formas de contato.</small>
                                </div>
                            </div>

                            <div class="row gutters align-items-end">
                                <div class="col-lg-3 col-md-6">
                                    <div class="form-group">
                                        <label for="natural_phone_type">Tipo</label>
                                        <Select2
                                                id="natural_phone_type"
                                                v-model="phoneDraft.type"
                                                :settings="select2Settings"
                                                :options="phoneTypeOptions"
                                                placeholder="Selecione"
                                        />
                                    </div>
                                </div>
                                <div class="col-lg-3 col-md-6">
                                    <div class="form-group">
                                        <label for="natural_phone_number">Numero</label>
                                        <VueTelInput
                                                id="natural_phone_number"
                                                mode="national"

                                                v-model="phoneDraft.number"
                                                :autoFormat="true"
                                                :preferredCountries="['US', 'DE', 'FR', 'CN']"
                                                :defaultCountry="phoneDraft.code_country"
                                                @country-changed="changePhoneCountry"
                                                :dropdownOptions="phoneDropdownOptions"
                                                :inputOptions="phoneInputOptions"
                                        />
                                    </div>
                                </div>
                                <div class="col-lg-4 col-md-8">
                                    <div class="form-group">
                                        <label for="natural_phone_description">Observacao</label>
                                        <input id="natural_phone_description" v-model="phoneDraft.description"
                                               type="text" class="form-control"
                                               placeholder="Ex.: WhatsApp, horario comercial...">
                                    </div>
                                </div>
                                <div class="col-lg-2 col-md-4">
                                    <button type="button" class="btn btn-primary phone-panel__add" @click="addPhone">
                                        <i class="icon-add"></i>
                                        Adicionar
                                    </button>
                                </div>
                            </div>
                            <div style="color: red" v-if="validate.phones"> {{ validate.phones }}</div>
                            <div v-if="phones.length" class="phone-list">
                                <div v-for="(phone, index) in phones" :key="index" class="phone-list__item">
                                    <span class="phone-list__icon"><i class="icon-phone"></i></span>
                                    <div class="phone-list__content">
                                        <strong>{{ getPhoneTypeLabel(phone.type) }} · {{ phone.code_country }}
                                            {{ phone.number }}</strong>
                                        <small>{{ phone.description || 'Sem observacao' }}</small>
                                    </div>
                                    <button type="button" class="phone-list__remove" aria-label="Remover telefone"
                                            @click="removePhone(index)">
                                        <i class="icon-trash-2"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else key="legal" class="person-form-card">
                <div class="person-form-card__header">
                    <div>
                        <span class="person-form-card__eyebrow">Pessoa jurídica</span>
                        <h5>Dados da empresa</h5>
                    </div>
                    <span class="person-form-card__badge">CNPJ identificado</span>
                </div>

                <div class="row gutters">
                    <div class="col-lg-6 col-md-12">
                        <div class="form-group">
                            <label for="name" class="required">{{ t('COMPANIES.NAME') }}</label>
                            <input id="name" v-model="legalPerson.name" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="validate.name"> {{ validate.name }}</div>
                        </div>
                    </div>
                    <div class="col-lg-6 col-md-12">
                        <div class="form-group">
                            <label for="trade_name" :class="{'required':requiredClassValidation('trade_name')}">{{
                                    t('CLIENTS.TRADE_NAME')
                                }}</label>
                            <input id="trade_name" v-model="legalPerson.trade_name" type="text" class="form-control">
                            <div style="color: red" v-if="validate.trade_name"> {{ validate.trade_name }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="email" :class="{'required':requiredClassValidation('email')}">{{
                                    t('GENERAL.EMAIL')
                                }}</label>
                            <input id="email" v-model="legalPerson.email" type="text" class="form-control">
                            <div style="color: red" v-if="validate.email"> {{ validate.email }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="birthday" :class="{'required':requiredClassValidation('birthday')}">{{
                                    t('CLIENTS.FOUNDATION_DAY')
                                }}</label>
                            <input id="birthday" v-model="legalPerson.birthday" type="date" class="form-control">
                            <div style="color: red" v-if="validate.birthday"> {{ validate.birthday }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="creci" :class="{'required':requiredClassValidation('creci')}">{{
                                    t('CLIENTS.CRECI')
                                }}</label>
                            <input id="creci" v-model="legalPerson.creci" type="text" class="form-control">
                            <div style="color: red" v-if="validate.creci"> {{ validate.creci }}</div>
                        </div>
                    </div>
                    <div class="col-lg-12">
                        <div class="form-group">
                            <label for="description" :class="{'required':requiredClassValidation('description')}">Observação</label>
                            <textarea id="description" v-model="legalPerson.description" type="text"
                                      class="form-control">
                            </textarea>
                            <div style="color: red" v-if="validate.description"> {{ validate.description }}</div>
                        </div>
                    </div>
                    <div class="col-12">
                        <div class="phone-panel">
                            <div class="phone-panel__header">
                                <span class="phone-panel__icon"><i class="icon-phone"></i></span>
                                <div>
                                    <strong :class="{'required':requiredClassValidation('phones')}">Telefones</strong>
                                    <small>Adicione telefone comercial, WhatsApp ou outros contatos da empresa.</small>
                                </div>
                            </div>

                            <div class="row gutters align-items-end">
                                <div class="col-lg-3 col-md-6">
                                    <div class="form-group">
                                        <label for="legal_phone_type">Tipo</label>
                                        <Select2
                                                id="legal_phone_type"
                                                v-model="phoneDraft.type"
                                                :settings="select2Settings"
                                                :options="phoneTypeOptions"
                                                placeholder="Selecione"
                                        />
                                    </div>
                                </div>
                                <div class="col-lg-3 col-md-6">
                                    <div class="form-group">
                                        <label for="legal_phone_number">Numero</label>
                                        <VueTelInput
                                                id="legal_phone_number"
                                                mode="national"
                                                v-model="phoneDraft.number"
                                                :autoFormat="true"
                                                :preferredCountries="['US', 'DE', 'FR', 'CN']"
                                                :defaultCountry="phoneDraft.code_country"
                                                :dropdownOptions="phoneDropdownOptions"
                                                :inputOptions="phoneInputOptions"
                                                @country-changed="changePhoneCountry"
                                        />
                                    </div>
                                </div>
                                <div class="col-lg-4 col-md-8">
                                    <div class="form-group">
                                        <label for="legal_phone_description">Observacao</label>
                                        <input id="legal_phone_description" v-model="phoneDraft.description" type="text"
                                               class="form-control" placeholder="Ex.: financeiro, recepcao...">
                                    </div>
                                </div>
                                <div class="col-lg-2 col-md-4">
                                    <button type="button" class="btn btn-primary phone-panel__add" @click="addPhone">
                                        <i class="icon-add"></i>
                                        Adicionar
                                    </button>
                                </div>
                            </div>
                            <div style="color: red" v-if="validate.phones"> {{ validate.phones }}</div>
                            <div v-if="phones.length" class="phone-list">
                                <div v-for="(phone, index) in phones" :key="index" class="phone-list__item">
                                    <span class="phone-list__icon"><i class="icon-phone"></i></span>
                                    <div class="phone-list__content">
                                        <strong>{{ getPhoneTypeLabel(phone.type) }} · {{ phone.number }}</strong>
                                        <small>{{ phone.description || 'Sem observacao' }}</small>
                                    </div>
                                    <button type="button" class="phone-list__remove" aria-label="Remover telefone"
                                            @click="removePhone(index)">
                                        <i class="icon-trash-2"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>
<script>
import {useToast} from 'vue-toastification';
import {useI18n} from 'vue-i18n';
import Select2 from 'vue3-select2-component';
import {VueTelInput} from 'vue-tel-input';
import 'vue-tel-input/dist/vue-tel-input.css';
import {mapActions, mapMutations, mapState} from 'vuex';
import AttributesValidationsMixin from '../../../mixins/AttributesValidationsMixin';
import {validateCpf, validateCnpj, formatCnpj} from '@/Helpers/Taxpayer';

export default {
    name: "createPublicPersonStepOne",
    components: {Select2, VueTelInput},
    mixins: [AttributesValidationsMixin],
    setup() {
        const toast = useToast();
        const {t} = useI18n();
        return {t, toast}
    },
    mounted() {
        if (this.clientPublicType === 'natural') {
            if (this.naturalPerson.taxpayer) {
                this.taxpayer = this.naturalPerson.taxpayer;
                this.personType = 'natural';
                this.phones = this.naturalPerson.phones ? this.naturalPerson.phones : [];
            }
        } else if (this.clientPublicType === 'legal') {
            if (this.legalPerson.taxpayer) {
                this.taxpayer = this.legalPerson.taxpayer;
                this.personType = 'legal';
                this.phones = this.legalPerson.phones ? this.legalPerson.phones : [];
            }
        }
    },
    data() {
        return {
            fieldsForValidationNaturalPerson: ['description', 'birthday', 'sex', 'rg', 'dispatch_date',
                'dispatching_agency', 'profession_id', 'income', 'marital_status_id', 'wedding_date',
                'marriage_certificate_registration', 'income_family', 'work_on_company', 'email', 'nationality', 'naturalness',
                'mother_name', 'father_name'],
            fieldsForValidationLegalPerson:['name', 'trade_name', 'email', 'birthday', 'creci', 'description'],
            validate: {},
            taxpayer: '',
            personType: '',
            phoneDraft: {
                type: null,
                number: null,
                code_country: 55,
                country_acronym: 'BR',
                description: '',
            },
            phones: [],
            phoneDropdownOptions: {
                showDialCodeInList: true,
                showDialCodeInSelection: true,
                showFlags: true,
                showSearchBox: true,
                tabindex: 3,
            },
            phoneInputOptions: {
                autocomplete: 'on',
                autofocus: true,
                'aria-describedby': 'des',
                showDialCode: false,
                placeholder: 'Numero Telefone',
                tabindex: 3,
                maxlength: 19,
            },
            select2Settings: {
                width: '100%',
            },
            phoneTypeOptions: [
                {id: 0, text: 'Whatsapp'},
                {id: 1, text: 'Ligação'},
                {id: 2, text: 'Ligação e Whatsapp'}
            ],
            optionsSex: ['MASCULINO', 'FEMININO', 'OUTROS'],
            professionOptions: {
                width: '100%',
                language: {
                    inputTooShort: function () {
                        return "Por favor, insira ao menos 3 caracteres para pesquisar.";
                    },
                    noResults: function () {
                        return "Nenhum resultado encontrado!";
                    }
                },
                minimumInputLength: 3,
                ajax: {
                    url: `${process.env.VUE_APP_BACK_END}/api/v1/publica/profissoes/listar`,
                    dataType: "json",
                    data: params => {
                        return {
                            term: params.term,
                        };
                    },
                    processResults: function (data) {
                        if (!(data.validations)) {
                            data.unshift({id: 0, text: "Nenhum"});
                            return {
                                results: data.map(({id, text: text}) => ({id, text}))
                            };
                        } else {
                            return {results: ""};
                        }
                    },
                }
            },
        }
    },
    computed: {
        ...mapState({
            attrsPhisical: (state) => state.publicClient.attrsPhisical,
            attrsJuridical: (state) => state.publicClient.attrsJuridical,
            clientPublicType: (state) => state.publicClient.clientPublicType,
            maritalStatusesOptions: (state) => state.publicClient.maritalStatusesOptions,
            naturalPerson: (state) => state.publicClient.naturalPerson,
            legalPerson: (state) => state.publicClient.legalPerson,
        }),
        cleanTaxpayer() {
            return this.onlyDigits(this.taxpayer);
        },
        hasInvalidTaxpayer() {
            return (this.cleanTaxpayer.length === 11 && !validateCpf(this.cleanTaxpayer))
                    || (this.cleanTaxpayer.length === 14 && !validateCnpj(this.cleanTaxpayer));
        },
        selectedPersonLabel() {
            if (this.personType === 'natural') return 'Pessoa fisica';
            if (this.personType === 'legal') return 'Pessoa jurídica';
            return 'Aguardando identificação';
        },
        taxpayerHint() {
            if (!this.cleanTaxpayer) return 'Digite um CPF ou CNPJ para liberar o formulário.';
            if (this.cleanTaxpayer.length < 11) return 'Continue digitando para identificarmos o tipo de cadastro.';
            if (this.hasInvalidTaxpayer) return 'Documento invalido. Confira os numeros para liberar o formulario.';
            if (this.cleanTaxpayer.length > 11 && this.cleanTaxpayer.length < 14) return 'Continue digitando para completar o CNPJ.';
            if (this.personType === 'natural') return 'CPF identificado. Confira e complete os dados da pessoa fisica.';
            return 'CNPJ identificado. Confira e complete os dados da pessoa jurídica.';
        },
        shouldShowForm() {
            return this.cleanTaxpayer.length >= 11 && !!this.personType;
        },
    },
    methods: {
        ...mapMutations({
            clientPublicTypeMutation: 'clientPublicTypeMutation',
            setNaturalPersonPublicMutation: 'setNaturalPersonPublicMutation',
            setLegalPersonPublicMutation: 'setLegalPersonPublicMutation',
            changeConjugeRequiredPersonPublicMutation: 'changeConjugeRequiredPersonPublicMutation',
        }),
        ...mapActions({findClientPublicAction:'findClientPublicAction'}),
        validateNaturalPerson() {
            this.naturalPerson.taxpayer = this.taxpayer;
            if (this.phoneDraft.type && this.phoneDraft.number) {
                this.pushInPhones();
            }
            this.naturalPerson.phones = this.phones;
            this.setVerifyInformedFieldNaturalPerson('name');
            this.setVerifyInformedFieldNaturalPerson('taxpayer');
            if (this.attrsPhisical.find(atr => atr.name === 'phones' && atr.is_required === true)) {
                if (!this.naturalPerson.phones.length) {
                    this.validate.phones = 'Necessario informar um telefone';
                } else {
                    this.validate.phones = null;
                }
            }
            this.fieldsForValidationNaturalPerson.forEach(i => {
                if (this.attrsPhisical.find(atr => atr.name === i && atr.is_required === true)) {
                    this.setVerifyInformedFieldNaturalPerson(i);
                }
            })
            let can_continue = true;
            Object.entries(this.validate).forEach(([index]) => {
                if (this.validate[index]) {
                    can_continue = false;
                }
            })
            this.setNaturalPersonPublicMutation(this.naturalPerson);
            return can_continue;
        },
        validateLegalPerson() {
            this.legalPerson.taxpayer = this.taxpayer;
            if (this.phoneDraft.type && this.phoneDraft.number) {
                this.pushInPhones();
            }
            this.legalPerson.phones = this.phones;
            this.setVerifyInformedFieldNaturalPerson('name', 'legal');
            this.setVerifyInformedFieldNaturalPerson('taxpayer', 'legal');
            if (this.attrsJuridical.find(atr => atr.name === 'phones' && atr.is_required === true)) {
                if (!this.legalPerson.phones.length) {
                    this.validate.phones = 'Necessario informar um telefone';
                } else {
                    this.validate.phones = null;
                }
            }
            this.fieldsForValidationLegalPerson.forEach(i => {
                if (this.attrsJuridical.find(atr => atr.name === i && atr.is_required === true)) {
                    this.setVerifyInformedFieldNaturalPerson(i, 'legal');
                }
            })
            let can_continue = true;
            Object.entries(this.validate).forEach(([index]) => {
                if (this.validate[index]) {
                    can_continue = false;
                }
            })
            this.setLegalPersonPublicMutation(this.legalPerson);
            return can_continue;
        },
        setVerifyInformedFieldNaturalPerson(index, type = 'natural') {
            if (type === 'natural') {
                if (!this.naturalPerson[index]) {
                    this.validate[index] = 'Campo Obrigatório';
                } else {
                    this.validate[index] = null;
                }
            } else if (type === 'legal') {
                if (!this.legalPerson[index]) {
                    this.validate[index] = 'Campo Obrigatório';
                } else {
                    this.validate[index] = null;
                }
            }
        },
        selectMaritalStatus(m) {
            this.changeConjugeRequiredPersonPublicMutation(m.spouse_signature_required);
            if (m.id === 'selecione') {
                this.naturalPerson.marital_status_id = null;
            }
        },
       async focusTaxpayer() {
            let tax = btoa(this.taxpayer);
            await this.findClientPublicAction({taxOrId:tax, companyId: this.$route.params.companyId}).then(resp =>{
                if (resp.data?.validations){
                    const digits = this.onlyDigits(this.taxpayer);
                    if (digits.length === 11 && validateCpf(digits)) {
                        this.clientPublicTypeMutation('natural');
                    } else if (digits.length === 14 && validateCnpj(digits)) {
                        this.clientPublicTypeMutation('legal');
                    }
                }else{
                    this.clientPublicTypeMutation(null);
                    this.taxpayer = null;
                    this.legalPerson = {};
                    this.naturalPerson = {};
                    this.personType = '';
                    this.setLegalPersonPublicMutation({});
                    this.setNaturalPersonPublicMutation({});
                    this.toast.success('CPF/CNPJ informado já possui cadastro no sistema!')
                }
            }).finally(() => this.$store.commit("changeLoading", false));
        },
        onlyDigits(value) {
            return String(value || '').replace(/[.\-/\s]/g, '').slice(0, 14);
        },
        handleTaxpayerInput() {
            const digits = this.onlyDigits(this.taxpayer);
            this.personType = '';

            if (digits.length === 11 && validateCpf(digits)) {
                this.personType = 'natural';
            }

            if (digits.length === 14 && validateCnpj(digits)) {
                this.personType = 'legal';
            }

            this.taxpayer = this.formatTaxpayer(digits);
        },
        selectPersonType(type) {
            if (type === 'natural' && !validateCpf(this.cleanTaxpayer)) return;
            if (type === 'legal' && !validateCnpj(this.cleanTaxpayer)) return;

            this.personType = type;
        },
        addPhone() {
            if (!this.phoneDraft.type || !this.phoneDraft.number) {
                this.toast.info('Informe o tipo e o numero do telefone.');
                return;
            }
            this.pushInPhones();
        },
        pushInPhones() {
            this.phones.push({
                type: this.phoneDraft.type,
                number: this.phoneDraft.number,
                code_country: this.phoneDraft.code_country,
                country_acronym: this.phoneDraft.country_acronym,
                description: this.phoneDraft.description,
            });
            this.phoneDraft = {
                type: null,
                number: '',
                code_country: 55,
                country_acronym: 'BR',
                description: '',
            };
        },
        changePhoneCountry(country) {
            this.phoneDraft.code_country = country?.dialCode || 55;
            this.phoneDraft.country_acronym = country?.iso2 || 'BR';
        },
        removePhone(index) {
            this.phones.splice(index, 1);
        },
        getPhoneTypeLabel(type) {
            return this.phoneTypeOptions.find(option => option.id === type)?.text || 'Telefone';
        },
        formatTaxpayer(value) {
            if (value.length > 11) {
                return formatCnpj(value);
            }

            return value
                    .replace(/^(\d{3})(\d)/, '$1.$2')
                    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
                    .replace(/\.(\d{3})(\d)/, '.$1-$2');
        },
    },
}
</script>
<style scoped>
.public-client-step-one {
    color: #243b35;
}

.step-one-hero {
    align-items: stretch;
    background: linear-gradient(135deg, #0e5e4e 0%, #16886f 54%, #f2fbf8 54%, #ffffff 100%);
    border-radius: 22px;
    box-shadow: 0 18px 44px rgba(14, 94, 78, .16);
    display: flex;
    gap: 20px;
    justify-content: space-between;
    margin-bottom: 16px;
    overflow: hidden;
    padding: 28px;
    position: relative;
}

.step-one-hero__content {
    max-width: 620px;
    position: relative;
    z-index: 1;
}

.step-one-hero__eyebrow,
.person-form-card__eyebrow {
    color: #55d5b6;
    display: inline-block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .12em;
    margin-bottom: 10px;
    text-transform: uppercase;
}

.step-one-hero__title {
    color: #ffffff;
    font-size: 28px;
    font-weight: 800;
    line-height: 1.2;
    margin-bottom: 10px;
}

.step-one-hero__text {
    color: rgba(255, 255, 255, .86);
    font-size: 15px;
    line-height: 1.6;
    margin: 0;
}

.step-one-hero__status {
    align-items: flex-start;
    align-self: center;
    background: rgba(255, 255, 255, .92);
    border: 1px solid rgba(255, 255, 255, .7);
    border-radius: 18px;
    box-shadow: 0 14px 35px rgba(24, 52, 45, .12);
    display: flex;
    flex-direction: column;
    min-width: 210px;
    padding: 18px;
}

.step-one-hero__status-icon {
    align-items: center;
    background: #e8faf5;
    border-radius: 14px;
    color: #0e5e4e;
    display: flex;
    font-size: 22px;
    height: 46px;
    justify-content: center;
    margin-bottom: 12px;
    width: 46px;
}

.step-one-hero__status small {
    color: #7a8c87;
    font-weight: 700;
    margin-bottom: 4px;
}

.step-one-hero__status strong {
    color: #173b33;
    font-size: 16px;
}

.document-card,
.person-form-card,
.empty-state-card {
    background: #ffffff;
    border: 1px solid #e4eee9;
    border-radius: 20px;
    box-shadow: 0 16px 36px rgba(21, 56, 48, .08);
}

.document-card {
    display: grid;
    gap: 18px;
    grid-template-columns: minmax(0, 1fr) 420px;
    margin-bottom: 16px;
    padding: 20px;
}

.document-input {
    position: relative;
}

.document-input__icon {
    color: #16886f;
    font-size: 18px;
    left: 17px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
}

.document-input__control {
    border-color: #d9e8e3;
    border-radius: 15px;
    font-size: 18px;
    font-weight: 700;
    height: 56px;
    letter-spacing: .04em;
    padding-left: 48px;
}

.document-input__control:focus {
    border-color: #19a985;
    box-shadow: 0 0 0 .2rem rgba(25, 169, 133, .16);
}

.document-card__options {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.person-type-card {
    background: #f8fbfa;
    border: 1px solid #e0ece7;
    border-radius: 16px;
    color: #38544d;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    min-height: 132px;
    padding: 16px;
    text-align: left;
    transition: all .2s ease;
}

.person-type-card span {
    align-items: center;
    background: #ffffff;
    border-radius: 12px;
    color: #16886f;
    display: flex;
    font-size: 20px;
    height: 42px;
    justify-content: center;
    margin-bottom: 12px;
    width: 42px;
}

.person-type-card strong {
    color: #1b3b34;
    font-size: 15px;
    margin-bottom: 4px;
}

.person-type-card small {
    color: #7b908a;
    line-height: 1.35;
}

.person-type-card:hover,
.person-type-card--active {
    background: #ecfbf7;
    border-color: #26b390;
    box-shadow: 0 10px 24px rgba(22, 136, 111, .14);
    transform: translateY(-1px);
}

.empty-state-card {
    align-items: center;
    display: flex;
    gap: 18px;
    padding: 24px;
}

.empty-state-card__icon {
    align-items: center;
    background: #eef8f5;
    border-radius: 18px;
    color: #0e5e4e;
    display: flex;
    flex: 0 0 58px;
    font-size: 24px;
    height: 58px;
    justify-content: center;
}

.empty-state-card h5,
.person-form-card h5 {
    color: #173b33;
    font-weight: 800;
    margin-bottom: 4px;
}

.empty-state-card p {
    color: #71857f;
    margin: 0;
}

.person-form-card {
    padding: 24px;
}

.person-form-card__header {
    align-items: center;
    border-bottom: 1px solid #e6efeb;
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 18px;
}

.person-form-card__eyebrow {
    color: #16886f;
    margin-bottom: 6px;
}

.person-form-card__badge {
    background: #e9fbf6;
    border-radius: 999px;
    color: #0e5e4e;
    font-size: 12px;
    font-weight: 800;
    padding: 8px 12px;
}

.person-form-card .form-control {
    border-color: #dce9e4;
    border-radius: 12px;
    min-height: 44px;
}

.person-form-card .form-control:focus {
    border-color: #19a985;
    box-shadow: 0 0 0 .2rem rgba(25, 169, 133, .14);
}

.person-form-card label,
.document-card label {
    color: #344f48;
    font-weight: 800;
}

.person-form-card :deep(.select2-container) {
    display: block;
    width: 100% !important;
}

.person-form-card :deep(.select2-container--default .select2-selection--single) {
    align-items: center;
    background-color: #ffffff;
    border-color: #dce9e4;
    border-radius: 12px !important;
    display: flex;
    height: 44px;
    min-height: 44px;
    overflow: hidden;
}

.person-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__rendered),
.person-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__arrow) {
    border-radius: 12px;
}

.person-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
    color: #243b35;
    line-height: 42px;
    padding-left: 14px;
    padding-right: 34px;
    width: 100%;
}

.person-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__arrow) {
    height: 42px;
    right: 8px;
}

.person-form-card :deep(.select2-container--default.select2-container--focus .select2-selection--single),
.person-form-card :deep(.select2-container--default .select2-selection--single:focus) {
    border-color: #19a985;
    box-shadow: 0 0 0 .2rem rgba(25, 169, 133, .14);
    outline: 0;
}

.person-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__placeholder) {
    color: #7f918c;
}

.phone-panel {
    background: linear-gradient(180deg, #f8fdfb 0%, #ffffff 100%);
    border: 1px solid #dfede8;
    border-radius: 18px;
    margin-top: 8px;
    padding: 18px;
}

.phone-panel__header {
    align-items: center;
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
}

.phone-panel__icon,
.phone-list__icon {
    align-items: center;
    background: #e9fbf6;
    border-radius: 14px;
    color: #0e5e4e;
    display: flex;
    flex: 0 0 44px;
    font-size: 20px;
    height: 44px;
    justify-content: center;
    width: 44px;
}

.phone-panel__header strong {
    color: #173b33;
    display: block;
    font-size: 15px;
    font-weight: 800;
    margin-bottom: 2px;
}

.phone-panel__header small {
    color: #71857f;
    display: block;
    line-height: 1.4;
}

.phone-panel__add {
    border-radius: 12px;
    font-weight: 800;
    min-height: 44px;
    width: 100%;
}

.phone-panel :deep(.vue-tel-input) {
    border: 1px solid #dce9e4;
    border-radius: 12px;
    min-height: 44px;
    overflow: visible;
}

.phone-panel :deep(.vue-tel-input:focus-within) {
    border-color: #19a985;
    box-shadow: 0 0 0 .2rem rgba(25, 169, 133, .14);
}

.phone-panel :deep(.vti__dropdown) {
    background: #f8fbfa;
    border-radius: 12px 0 0 12px;
}

.phone-panel :deep(.vti__dropdown-list) {
    border-radius: 12px;
    box-shadow: 0 14px 35px rgba(24, 52, 45, .16);
    z-index: 1050;
}

.phone-panel :deep(.vti__input) {
    color: #243b35;
    min-height: 42px;
}

.phone-list {
    display: grid;
    gap: 10px;
    margin-top: 14px;
}

.phone-list__item {
    align-items: center;
    background: #ffffff;
    border: 1px solid #e0ece7;
    border-radius: 16px;
    display: grid;
    gap: 12px;
    grid-template-columns: 44px minmax(0, 1fr) 38px;
    padding: 12px;
}

.phone-list__content strong {
    color: #173b33;
    display: block;
    font-weight: 800;
    margin-bottom: 3px;
}

.phone-list__content small {
    color: #71857f;
}

.phone-list__remove {
    align-items: center;
    background: #ffffff;
    border: 1px solid #e4eee9;
    border-radius: 12px;
    color: #b53f3f;
    cursor: pointer;
    display: flex;
    height: 38px;
    justify-content: center;
    width: 38px;
}

html.dark-theme .public-client-step-one {
    color: var(--dark-text-soft);
}

html.dark-theme .step-one-hero {
    background: linear-gradient(135deg, #064e3b 0%, #0f766e 52%, var(--dark-surface-2) 52%, var(--dark-surface) 100%);
    box-shadow: var(--dark-shadow);
}

html.dark-theme .document-card,
html.dark-theme .person-form-card,
html.dark-theme .empty-state-card,
html.dark-theme .step-one-hero__status,
html.dark-theme .phone-panel,
html.dark-theme .phone-list__item {
    background: var(--dark-surface) !important;
    border-color: var(--dark-border) !important;
    box-shadow: var(--dark-shadow) !important;
}

html.dark-theme .step-one-hero__status {
    background: rgba(17, 24, 39, .92) !important;
}

html.dark-theme .step-one-hero__status-icon,
html.dark-theme .empty-state-card__icon,
html.dark-theme .phone-panel__icon,
html.dark-theme .phone-list__icon,
html.dark-theme .person-type-card span {
    background: var(--dark-primary-soft) !important;
    color: var(--dark-primary) !important;
}

html.dark-theme .step-one-hero__status strong,
html.dark-theme .empty-state-card h5,
html.dark-theme .person-form-card h5,
html.dark-theme .phone-panel__header strong,
html.dark-theme .phone-list__content strong,
html.dark-theme .person-type-card strong {
    color: var(--dark-text) !important;
}

html.dark-theme .step-one-hero__status small,
html.dark-theme .empty-state-card p,
html.dark-theme .phone-panel__header small,
html.dark-theme .phone-list__content small,
html.dark-theme .person-type-card small,
html.dark-theme .form-text,
html.dark-theme .text-muted {
    color: var(--dark-text-muted) !important;
}

html.dark-theme .document-input__control,
html.dark-theme .person-form-card .form-control,
html.dark-theme .phone-panel :deep(.vue-tel-input),
html.dark-theme .person-form-card :deep(.select2-container--default .select2-selection--single) {
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-text-soft) !important;
}

html.dark-theme .person-form-card label,
html.dark-theme .document-card label,
html.dark-theme .phone-panel :deep(.vti__input),
html.dark-theme .person-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
    color: var(--dark-text-soft) !important;
}

html.dark-theme .document-input__control::placeholder,
html.dark-theme .person-form-card .form-control::placeholder,
html.dark-theme .phone-panel :deep(.vti__input::placeholder) {
    color: var(--dark-text-muted) !important;
}

html.dark-theme .person-type-card {
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
}

html.dark-theme .person-type-card:hover,
html.dark-theme .person-type-card--active {
    background: var(--dark-primary-soft) !important;
    border-color: var(--dark-primary) !important;
}

html.dark-theme .person-form-card__badge {
    background: var(--dark-primary-soft) !important;
    color: var(--dark-primary) !important;
}

html.dark-theme .phone-list__remove {
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-danger) !important;
}

html.dark-theme .phone-panel :deep(.vti__dropdown),
html.dark-theme .phone-panel :deep(.vti__dropdown-list) {
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-text-soft) !important;
}

.step-one-fade-enter-active,
.step-one-fade-leave-active {
    transition: opacity .18s ease, transform .18s ease;
}

.step-one-fade-enter-from,
.step-one-fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
}

@media (max-width: 991.98px) {
    .step-one-hero,
    .document-card {
        grid-template-columns: 1fr;
    }

    .step-one-hero {
        background: linear-gradient(160deg, #0e5e4e 0%, #16886f 100%);
        flex-direction: column;
    }

    .step-one-hero__status {
        align-self: stretch;
    }

    .document-card__options {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 575.98px) {
    .public-client-step-one {
        margin-left: -6px;
        margin-right: -6px;
    }

    .step-one-hero,
    .document-card,
    .person-form-card,
    .empty-state-card {
        border-radius: 16px;
        padding: 16px 12px;
    }

    .step-one-hero {
        gap: 14px;
        margin-bottom: 12px;
    }

    .step-one-hero__eyebrow {
        font-size: 10px;
        margin-bottom: 8px;
    }

    .step-one-hero__title {
        font-size: 21px;
        line-height: 1.22;
        margin-bottom: 8px;
    }

    .step-one-hero__text {
        font-size: 13px;
        line-height: 1.45;
    }

    .step-one-hero__status {
        align-items: center;
        border-radius: 14px;
        display: grid;
        gap: 4px 10px;
        grid-template-columns: 40px minmax(0, 1fr);
        min-width: 0;
        padding: 12px;
    }

    .step-one-hero__status-icon {
        grid-row: span 2;
        height: 40px;
        margin-bottom: 0;
        width: 40px;
    }

    .step-one-hero__status small,
    .step-one-hero__status strong {
        margin-bottom: 0;
    }

    .document-card {
        gap: 12px;
        margin-bottom: 12px;
    }

    .document-input__control {
        font-size: 16px;
        height: 50px;
    }

    .document-card__options {
        display: none;
    }

    .empty-state-card,
    .person-form-card__header,
    .phone-panel__header {
        align-items: flex-start;
        flex-direction: column;
    }

    .empty-state-card {
        gap: 12px;
    }

    .empty-state-card__icon {
        flex-basis: 46px;
        height: 46px;
        width: 46px;
    }

    .empty-state-card h5,
    .person-form-card h5 {
        font-size: 17px;
    }

    .empty-state-card p {
        font-size: 13px;
        line-height: 1.45;
    }

    .person-form-card {
        padding: 16px 12px;
    }

    .person-form-card__header {
        gap: 10px;
        margin-bottom: 14px;
        padding-bottom: 14px;
    }

    .person-form-card__eyebrow {
        font-size: 10px;
        margin-bottom: 5px;
    }

    .person-form-card__badge {
        font-size: 11px;
        padding: 7px 10px;
    }

    .person-form-card .row.gutters > [class*="col-"] {
        margin-bottom: 12px;
    }

    .person-form-card .form-group,
    .person-form-card .form-group.mb-0,
    .person-form-card .form-group.mb-lg-0 {
        margin-bottom: 0 !important;
    }

    .person-form-card label,
    .document-card label {
        font-size: 12px;
        margin-bottom: 6px;
    }

    .person-form-card .form-control,
    .person-form-card :deep(.select2-container--default .select2-selection--single),
    .phone-panel :deep(.vue-tel-input) {
        border-radius: 11px;
        min-height: 42px;
    }

    .person-form-card textarea.form-control {
        min-height: 86px;
    }

    .phone-panel {
        border-radius: 15px;
        margin-top: 2px;
        padding: 14px 10px;
    }

    .phone-panel__header {
        gap: 10px;
        margin-bottom: 12px;
    }

    .phone-panel__icon,
    .phone-list__icon {
        flex-basis: 40px;
        height: 40px;
        width: 40px;
    }

    .phone-panel__header strong {
        font-size: 14px;
    }

    .phone-panel__header small {
        font-size: 12px;
    }

    .phone-panel__add {
        min-height: 42px;
    }

    .phone-list {
        gap: 8px;
        margin-top: 12px;
    }

    .phone-list__item {
        align-items: flex-start;
        border-radius: 14px;
        gap: 10px;
        grid-template-columns: 40px minmax(0, 1fr) 36px;
        padding: 10px;
    }

    .phone-list__content strong {
        font-size: 13px;
        overflow-wrap: anywhere;
    }

    .phone-list__content small {
        font-size: 12px;
    }

    .phone-list__remove {
        height: 36px;
        width: 36px;
    }

    .phone-panel :deep(.vti__dropdown-list) {
        left: -1px;
        max-width: calc(100vw - 44px);
        min-width: 280px;
    }

    .phone-panel :deep(.vti__input) {
        font-size: 14px;
        min-width: 0;
    }
}

@media (max-width: 991.98px) {
    html.dark-theme .step-one-hero {
        background: linear-gradient(160deg, #064e3b 0%, var(--dark-surface-2) 100%) !important;
    }
}

</style>
